import React, { useState } from 'react';
import { apiPut } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import { Btn, Card, inputCls, fmtDate, errMsg } from './adminUi';
import { ConfirmModal } from './AdminActionModals';

/**
 * Everee API access for this company: the tenant ID + API token Everee emails to Empivo after verifying the company,
 * and the switch that turns the payroll service on for the company.
 */
const AdminEvereeCredentials = ({ rec, id, onUpdated }) => {
  const notification = useToastContext();
  const cred = rec.credentials || { exists: false, status: 'inactive' };
  const sent = ['Submitted to Everee', 'Active'].includes(rec.status);
  const isActive = cred.status === 'active';

  const [tenantId, setTenantId] = useState(cred.tenantId || '');
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [failed, setFailed] = useState(false); // last save attempt was rejected by Everee

  const base = `${apiPath.payrollAdminBase}/${id}/credentials`;

  const call = async (req, okMsg) => {
    try {
      setBusy(true);
      const res = await req();
      if (res?.data?.success) {
        onUpdated(res.data.results);
        notification.success(okMsg);
        return true;
      }
      notification.error(errMsg(res, 'Action failed'));
    } catch (e) {
      notification.error('Action failed');
    } finally {
      setBusy(false);
    }
    return false;
  };

  const save = async () => {
    setFailed(false); // reset the previous result while the new attempt runs
    if (await call(() => apiPut(base, { tenantId, apiToken }), 'Credentials verified with Everee and saved')) {
      setApiToken('');
      setShowToken(false);
    } else {
      setFailed(true);
    }
  };

  const toggle = async () => {
    await call(() => apiPut(`${base}/status`, { status: isActive ? 'inactive' : 'active' }), isActive ? 'Payroll service deactivated' : 'Payroll service activated');
    setConfirm(false);
  };

  const dirty = tenantId.trim() !== (cred.tenantId || '') || apiToken.trim() !== '';
  const canSave = !busy && tenantId.trim() && (cred.exists || apiToken.trim()) && dirty;

  return (
    <Card
      title="Everee API access"
      action={
        <span className={`text-[11px] font-bold uppercase tracking-wider ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
          {isActive ? '● Service active' : cred.exists ? 'Service inactive' : 'Not set up'}
        </span>
      }
    >
      {!sent ? (
        <p className="text-sm text-gray-500">
          Available after the package has been sent to Everee. Once Everee verifies the company they email Empivo the tenant ID and
          API token – enter them here.
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="everee-tenant" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Tenant ID</label>
            <input id="everee-tenant" className={inputCls} value={tenantId} onChange={(e) => { setTenantId(e.target.value); setFailed(false); }} autoComplete="off" placeholder="From Everee's email" />
          </div>

          <div>
            <label htmlFor="everee-token" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">API token</label>
            <div className="relative">
              <input
                id="everee-token"
                className={`${inputCls} pr-14`}
                type={showToken ? 'text' : 'password'}
                value={apiToken}
                onChange={(e) => { setApiToken(e.target.value); setFailed(false); }}
                autoComplete="new-password"
                placeholder={cred.exists ? `Saved (ends in ${cred.tokenLast4}) – leave blank to keep` : 'From Everee’s email'}
              />
              <button type="button" onClick={() => setShowToken((s) => !s)} className="absolute right-3 top-2 text-xs font-semibold text-blue-700 hover:underline">
                {showToken ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">Checked against Everee before saving. Stored encrypted and never displayed again.</p>
            {cred.verifiedAt && !dirty && !failed && !busy && <p className="mt-1 text-[11px] font-semibold text-green-700">✓ Verified with Everee {fmtDate(cred.verifiedAt)}</p>}
            {failed && <p className="mt-1 text-[11px] font-semibold text-red-600">✕ Not verified – these credentials were not saved.</p>}
          </div>

          <Btn variant="primary" className="w-full" disabled={!canSave} onClick={save}>{busy ? 'Verifying with Everee…' : 'Verify & save credentials'}</Btn>

          {/* service switch */}
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Payroll service</p>
              <p className="text-xs text-gray-500">
                {!cred.exists
                  ? 'Save the credentials to enable.'
                  : isActive
                  ? `Active${cred.activatedAt ? ` since ${fmtDate(cred.activatedAt)}` : ''}`
                  : 'Inactive – the company cannot use payroll yet.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              aria-label="Payroll service"
              disabled={!cred.exists || busy}
              onClick={() => setConfirm(true)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${isActive ? 'bg-green-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title={isActive ? 'Deactivate payroll service' : 'Activate payroll service'}
          body={
            isActive
              ? `Turn off payroll for ${rec.companyInfo.legalName}? They will no longer be able to use the payroll service.`
              : `Turn on payroll for ${rec.companyInfo.legalName}? Empivo will use the saved tenant ID and API token to call Everee on their behalf.`
          }
          confirmLabel={isActive ? 'Deactivate' : 'Activate'}
          variant={isActive ? 'danger' : 'success'}
          busy={busy}
          onConfirm={toggle}
          onClose={() => setConfirm(false)}
        />
      )}
    </Card>
  );
};

export default AdminEvereeCredentials;





// import React, { useState } from 'react';
// import { apiPut } from '../../../utils/apiFetch';
// import apiPath from '../../../utils/pathObj';
// import useToastContext from 'hooks/useToastContext';
// import { Btn, Card, inputCls, fmtDate } from './adminUi';
// import { ConfirmModal } from './AdminActionModals';

// /**
//  * Everee API access for this company: the tenant ID + API token Everee emails to Empivo after verifying the company,
//  * and the switch that turns the payroll service on for the company.
//  */
// const AdminEvereeCredentials = ({ rec, id, onUpdated }) => {
//   const notification = useToastContext();
//   const cred = rec.credentials || { exists: false, status: 'inactive' };
//   const sent = ['Submitted to Everee', 'Active'].includes(rec.status);
//   const isActive = cred.status === 'active';

//   const [tenantId, setTenantId] = useState(cred.tenantId || '');
//   const [apiToken, setApiToken] = useState('');
//   const [showToken, setShowToken] = useState(false);
//   const [busy, setBusy] = useState(false);
//   const [confirm, setConfirm] = useState(false);

//   const base = `${apiPath.payrollAdminBase}/${id}/credentials`;

//   const call = async (req, okMsg) => {
//     try {
//       setBusy(true);
//       const res = await req();
//       if (res?.data?.success) {
//         onUpdated(res.data.results);
//         notification.success(okMsg);
//         return true;
//       }
//       notification.error(res?.data?.message || 'Action failed');
//     } catch (e) {
//       notification.error('Action failed');
//     } finally {
//       setBusy(false);
//     }
//     return false;
//   };

//   const save = async () => {
//     if (await call(() => apiPut(base, { tenantId, apiToken }), 'Everee credentials saved')) {
//       setApiToken('');
//       setShowToken(false);
//     }
//   };

//   const toggle = async () => {
//     await call(() => apiPut(`${base}/status`, { status: isActive ? 'inactive' : 'active' }), isActive ? 'Payroll service deactivated' : 'Payroll service activated');
//     setConfirm(false);
//   };

//   const dirty = tenantId.trim() !== (cred.tenantId || '') || apiToken.trim() !== '';
//   const canSave = !busy && tenantId.trim() && (cred.exists || apiToken.trim()) && dirty;

//   return (
//     <Card
//       title="Everee API access"
//       action={
//         <span className={`text-[11px] font-bold uppercase tracking-wider ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
//           {isActive ? '● Service active' : cred.exists ? 'Service inactive' : 'Not set up'}
//         </span>
//       }
//     >
//       {!sent ? (
//         <p className="text-sm text-gray-500">
//           Available after the package has been sent to Everee. Once Everee verifies the company they email Empivo the tenant ID and
//           API token – enter them here.
//         </p>
//       ) : (
//         <div className="space-y-4">
//           <div>
//             <label htmlFor="everee-tenant" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Tenant ID</label>
//             <input id="everee-tenant" className={inputCls} value={tenantId} onChange={(e) => setTenantId(e.target.value)} autoComplete="off" placeholder="From Everee's email" />
//           </div>

//           <div>
//             <label htmlFor="everee-token" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">API token</label>
//             <div className="relative">
//               <input
//                 id="everee-token"
//                 className={`${inputCls} pr-14`}
//                 type={showToken ? 'text' : 'password'}
//                 value={apiToken}
//                 onChange={(e) => setApiToken(e.target.value)}
//                 autoComplete="new-password"
//                 placeholder={cred.exists ? `Saved (ends in ${cred.tokenLast4}) – leave blank to keep` : 'From Everee’s email'}
//               />
//               <button type="button" onClick={() => setShowToken((s) => !s)} className="absolute right-3 top-2 text-xs font-semibold text-blue-700 hover:underline">
//                 {showToken ? 'Hide' : 'Show'}
//               </button>
//             </div>
//             <p className="mt-1 text-[11px] text-gray-400">Stored encrypted. It is never displayed again after saving.</p>
//           </div>

//           <Btn variant="primary" className="w-full" disabled={!canSave} onClick={save}>{busy ? 'Saving…' : 'Save credentials'}</Btn>

//           {/* service switch */}
//           <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
//             <div>
//               <p className="text-sm font-semibold text-gray-900">Payroll service</p>
//               <p className="text-xs text-gray-500">
//                 {!cred.exists
//                   ? 'Save the credentials to enable.'
//                   : isActive
//                   ? `Active${cred.activatedAt ? ` since ${fmtDate(cred.activatedAt)}` : ''}`
//                   : 'Inactive – the company cannot use payroll yet.'}
//               </p>
//             </div>
//             <button
//               type="button"
//               role="switch"
//               aria-checked={isActive}
//               aria-label="Payroll service"
//               disabled={!cred.exists || busy}
//               onClick={() => setConfirm(true)}
//               className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${isActive ? 'bg-green-600' : 'bg-gray-300'}`}
//             >
//               <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
//             </button>
//           </div>
//         </div>
//       )}

//       {confirm && (
//         <ConfirmModal
//           title={isActive ? 'Deactivate payroll service' : 'Activate payroll service'}
//           body={
//             isActive
//               ? `Turn off payroll for ${rec.companyInfo.legalName}? They will no longer be able to use the payroll service.`
//               : `Turn on payroll for ${rec.companyInfo.legalName}? Empivo will use the saved tenant ID and API token to call Everee on their behalf.`
//           }
//           confirmLabel={isActive ? 'Deactivate' : 'Activate'}
//           variant={isActive ? 'danger' : 'success'}
//           busy={busy}
//           onConfirm={toggle}
//           onClose={() => setConfirm(false)}
//         />
//       )}
//     </Card>
//   );
// };

// export default AdminEvereeCredentials;
